import { differenceInYears } from 'date-fns/differenceInYears'
import type { i18n } from 'i18next'
import * as z from 'zod'

import type { IntlFormatter } from '@nxt-insurance/formatting'

const RADIO_TYPES = ['string', 'boolean', 'number', 'array'] as const

const isExpectedRadioType = (type: any): type is (typeof RADIO_TYPES)[number] => {
  return RADIO_TYPES.includes(type)
}

const isRadioError = (issue: z.ZodInvalidUnionIssue) => {
  if (issue.unionErrors.length !== 6) {
    return false
  }
  const invalidTypeOcurrences: Record<(typeof RADIO_TYPES)[number], number> = {
    string: 0,
    boolean: 0,
    number: 0,
    array: 0,
  }

  // The union error for radio/select elements is very specific
  return (
    issue.unionErrors.every((err) => {
      const unionIssue = err.issues[0]
      if (unionIssue.code === z.ZodIssueCode.invalid_type) {
        if (isExpectedRadioType(unionIssue.expected)) {
          invalidTypeOcurrences[unionIssue.expected] += 1
          return true
        }
        return true
      }
      return false
    }) &&
    invalidTypeOcurrences.string === 1 &&
    invalidTypeOcurrences.boolean === 1 &&
    invalidTypeOcurrences.number === 1 &&
    invalidTypeOcurrences.array === 3
  )
}

// This maps the error messages to one of our predefined keys so that
// the clients can use the right translation
// for more information about ZodIssueCode and ZodErrorMap:
// https://github.com/colinhacks/zod/blob/master/ERROR_HANDLING.md#zodissuecode
export const getDefaultErrorMap =
  ({ t }: i18n, formatter: IntlFormatter): z.ZodErrorMap =>
  (issue, ctx) => {
    switch (issue.code) {
      case z.ZodIssueCode.invalid_union:
        if (isRadioError(issue)) {
          return { message: t('validation:errors.boolean.chooseOneOption') }
        }
        break
      case z.ZodIssueCode.invalid_enum_value:
        return { message: t('validation:errors.boolean.chooseOneOption') }
      case z.ZodIssueCode.invalid_type:
        // Handles required values that received undefined
        if (issue.received === 'undefined') {
          if (issue.expected === 'boolean') {
            return { message: t('validation:errors.boolean.chooseOneOption') }
          }
          return { message: t('validation:errors.generic.required') }
        }
        if (issue.expected === 'number') {
          return { message: t('validation:errors.number.invalid') }
        }
        break
      case z.ZodIssueCode.invalid_literal:
        if (issue.expected === true) {
          return { message: t('validation:errors.generic.required') }
        }
        break
      case z.ZodIssueCode.invalid_date:
        return {
          message: t('validation:errors.date.invalid'),
        }
      case z.ZodIssueCode.too_small:
        if (issue.type === 'date') {
          const maxAge = differenceInYears(new Date(), new Date(Number(issue.minimum)))
          return {
            message: t('validation:errors.date.min', { max: maxAge }),
          }
        } else if (issue.type === 'string') {
          if (issue.minimum === 1) {
            return { message: t('validation:errors.generic.required') }
          } else if (issue.path.some((p) => String(p).includes('password'))) {
            return {
              message: t('validation:errors.auth.minPasswordLength', {
                min: issue.minimum,
              }),
            }
          }
          return {
            message: t('validation:errors.string.min', {
              min: issue.minimum,
            }),
          }
        } else if (issue.type === 'number') {
          return {
            message: t('validation:errors.number.min', {
              min: formatter.formatNumber(issue.minimum),
            }),
          }
        } else if (issue.type === 'array') {
          if (issue.minimum === 1) {
            return { message: t('validation:errors.generic.required') }
          }
        }
        break
      case z.ZodIssueCode.too_big:
        if (issue.type === 'date') {
          const minAge = differenceInYears(new Date(), new Date(Number(issue.maximum)))
          return {
            message: t('validation:errors.date.max', { min: minAge }),
          }
        } else if (issue.type === 'number') {
          return {
            message: t('validation:errors.number.max', {
              max: formatter.formatNumber(issue.maximum),
            }),
          }
        } else if (issue.type === 'string') {
          return {
            message: t('validation:errors.string.max'),
          }
        }
        break
      case z.ZodIssueCode.invalid_string:
        if (issue.validation === 'email') {
          return { message: t('validation:errors.string.email') }
        }
        break
      case z.ZodIssueCode.custom:
        if (issue.message !== undefined) {
          return { message: issue.message }
        }
        break
      default:
        return { message: t('validation:errors.default', { message: ctx.defaultError }) }
    }
    return { message: t('validation:errors.default', { message: ctx.defaultError }) }
  }
